/**
 * OcrGroupDetail — 前台用户批次 OCR 结果展开详情
 *
 * 图片展示策略：
 *   - 图片未被删除（is_exported=false）→ 展示真实图片缩略图
 *   - 图片已被删除（is_exported=true）  → 只展示手机号，不加载图片
 *     （调用方通过 imagesDeleted prop 告知）
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { OriginLine, describeOrigin } from './DuplicateOrigin';

function fmtPhone(p) {
  if (!p) return '';
  return String(p).replace(/\D/g, '');
}

const PAGE_SIZE = 10; // 成功配对列表每页条数

// 这几类失败重试或手动填写都没有意义（图片被识别服务拒绝 / 原图丢失 / 服务未配置），
// 用户端不给按钮，提示联系管理员。其余失败（超时、限流、网络等瞬时故障）允许用户自己重试。
const NO_USER_ACTION_KINDS = new Set(['api_4xx', 'api_error', 'config', 'file_missing']);

export default function OcrGroupDetail({ groupUUID, imagesDeleted, onManualFill, onReocr }) {
  const [detail, setDetail]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [expandedPairs, setExpandedPairs] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null); // 点击放大预览
  const [pairPage, setPairPage]   = useState(1); // 成功配对当前页（从1开始）

  useEffect(() => {
    if (!groupUUID) return;
    setLoading(true);
    setPairPage(1); // 数据刷新时重置到第1页
    api.get(`/images/ocr-detail/${groupUUID}`)
      .then(r => setDetail(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [groupUUID]);

  if (loading) return (
    <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--c-text-tertiary)' }}>加载中…</div>
  );
  if (!detail) return null;

  const imgs = detail.images || [];

  // 成功配对：按手机号聚合
  const pairMap = new Map();
  for (const img of imgs) {
    if (img.ocrResult !== 'qualified' && img.ocrResult !== 'manual_set') continue;
    const key = img.ocrPhone || `manual_${img.id}`;
    if (!pairMap.has(key)) pairMap.set(key, { isManual: false, imgList: [] });
    const entry = pairMap.get(key);
    entry.imgList.push(img);
    if (img.ocrResult === 'manual_set') entry.isManual = true;
  }

  // 重复：号码在别处（或本批已配对后多传的图）已成功提交，本次不计成功。
  // 此前这里没有 duplicate 分支，重复的图在用户视角下直接消失，用户不知道为什么这张图没算成功。
  const dupMap = new Map();
  for (const img of imgs) {
    if (img.ocrResult !== 'duplicate') continue;
    const key = img.ocrPhone || `dup_${img.id}`;
    if (!dupMap.has(key)) dupMap.set(key, { phone: img.ocrPhone, imgList: [] });
    dupMap.get(key).imgList.push(img);
  }
  const dupGroups = [...dupMap.values()];

  // 无效：2张配对但不在库/过期（有"组"的概念）
  const invalids = imgs.filter(i => {
    const r = i.ocrResult || '';
    return r.startsWith('invalid') && r !== 'invalid_single' && r !== 'invalid_notext';
  });
  // 失败：识别不出号码 + OCR崩溃 + 单张缺配对（统一"张"单位）
  const singles  = imgs.filter(i => i.ocrResult === 'invalid_single');
  const notexts  = imgs.filter(i => i.ocrResult === 'invalid_notext');
  const faileds  = imgs.filter(i => i.ocrResult === 'failed');

  return (
    <div style={{ borderTop: '1px solid var(--c-border)', padding: '10px 14px' }}>

      {/* ── 成功配对 ─────────────────────────────────────────────── */}
      {pairMap.size > 0 && (() => {
        const pairEntries = [...pairMap.entries()];
        const totalPages  = Math.ceil(pairEntries.length / PAGE_SIZE);
        const curPage     = Math.min(pairPage, totalPages);
        const pageEntries = pairEntries.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);
        return (
        <>
          {/* 标题行：左侧"成功配对"，右侧翻页控件（超过1页时显示） */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 7 }}>
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', fontWeight: 600, flex: 1 }}>
              成功配对
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <button
                  disabled={curPage <= 1}
                  onClick={() => setPairPage(p => Math.max(1, p - 1))}
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: '1px solid var(--c-border)',
                    background: curPage <= 1 ? 'var(--c-bg)' : 'var(--c-surface)',
                    color: curPage <= 1 ? 'var(--c-text-tertiary)' : 'var(--c-text)',
                    cursor: curPage <= 1 ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <span style={{ color: 'var(--c-text-secondary)', minWidth: 52, textAlign: 'center' }}>
                  {curPage} / {totalPages}
                </span>
                <button
                  disabled={curPage >= totalPages}
                  onClick={() => setPairPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: '1px solid var(--c-border)',
                    background: curPage >= totalPages ? 'var(--c-bg)' : 'var(--c-surface)',
                    color: curPage >= totalPages ? 'var(--c-text-tertiary)' : 'var(--c-text)',
                    cursor: curPage >= totalPages ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            )}
          </div>
          {pageEntries.map(([phone, { isManual, imgList }]) => {
            const isOpen = !!expandedPairs[phone];
            return (
              <div key={phone} style={{
                border: `1px solid ${isManual ? 'var(--c-purple)' : 'var(--c-border)'}`,
                borderRadius: 10, overflow: 'hidden', marginBottom: 8,
              }}>
                {/* 配对头：手机号 + badge，图片未删时可展开 */}
                <div
                  style={{
                    padding: '10px 14px',
                    background: isManual ? 'rgba(88,86,214,.07)' : 'var(--c-bg)',
                    display: 'flex', alignItems: 'center', gap: 10,
                    cursor: imagesDeleted ? 'default' : 'pointer',
                  }}
                  onClick={() => !imagesDeleted && setExpandedPairs(p => ({ ...p, [phone]: !p[phone] }))}
                >
                  <span style={{
                    fontSize: 16, fontWeight: 700, letterSpacing: '.08em', flex: 1,
                    color: isManual ? 'var(--c-purple)' : 'var(--c-text)',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {fmtPhone(phone)}
                  </span>
                  <span style={{
                    fontSize: 10, color: 'var(--c-text-tertiary)',
                    background: 'var(--c-bg)', borderRadius: 6,
                    padding: '1px 6px', border: '1px solid var(--c-border)',
                  }}>
                    {imgList.length} 张
                  </span>
                  {isManual
                    ? <span style={{ fontSize: 10, background: 'rgba(88,86,214,.15)', color: 'var(--c-purple)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>手动确认</span>
                    : <span style={{ fontSize: 10, background: 'rgba(52,199,89,.12)', color: 'var(--c-success)', borderRadius: 6, padding: '1px 7px', fontWeight: 600 }}>匹配成功</span>
                  }
                  {/* 图片未删才显示展开箭头 */}
                  {!imagesDeleted && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--c-text-tertiary)" strokeWidth="2"
                      style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '.15s', flexShrink: 0 }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  )}
                </div>

                {/* 展开图片（仅图片未删时） */}
                {isOpen && !imagesDeleted && (
                  <div style={{ padding: '8px 12px', display: 'flex', gap: 8, background: 'var(--c-surface)', flexWrap: 'wrap' }}>
                    {imgList.map(img => (
                      <div key={img.id} style={{ position: 'relative' }}>
                        <img
                          src={img.thumbUrl || img.url} alt=""
                          style={{
                            width: 52, height: 92,
                            objectFit: 'cover', borderRadius: 6,
                            border: `1.5px solid ${isManual ? 'var(--c-purple)' : 'var(--c-border)'}`,
                            display: 'block', cursor: 'zoom-in',
                          }}
                          onClick={() => setLightboxUrl(img.url)}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        {isManual && (
                          <div style={{
                            position: 'absolute', bottom: 4, right: 4,
                            background: 'var(--c-purple)', borderRadius: 4,
                            padding: '1px 4px', fontSize: 8, color: '#fff', fontWeight: 700,
                          }}>手动</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* 底部页码提示（超过1页时显示） */}
          {totalPages > 1 && (
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--c-text-tertiary)', marginBottom: 4 }}>
              第 {curPage} 页，共 {pairEntries.length} 条
            </div>
          )}
        </>
        );
      })()}

      {/* ── 重复：说明"为什么没算成功"，以及首次成功在哪里 ─────────────────────── */}
      {dupGroups.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '10px 0 7px' }}>重复号码（不计入成功）</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {dupGroups.map(({ phone, imgList }) => {
              const origin = imgList.find(i => i.originBatch)?.originBatch || null;
              const d = describeOrigin(origin, 'user');
              const tone = d.tone === 'danger' ? 'var(--c-danger)' : 'var(--c-accent)';
              return (
                <div key={phone || imgList[0].id} style={{
                  border: `1px solid ${tone}`, borderRadius: 10,
                  padding: '10px 14px', background: 'var(--c-surface)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  {!imagesDeleted ? (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {imgList.slice(0, 2).map(img => (
                        <img key={img.id} src={img.thumbUrl || img.url} alt=""
                          style={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
                          onClick={() => setLightboxUrl(img.url)}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ))}
                    </div>
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: tone, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '.04em' }}>{fmtPhone(phone) || '—'}</span>
                      <span style={{ fontSize: 10, borderRadius: 6, padding: '1px 7px', fontWeight: 600, background: d.tone === 'danger' ? 'rgba(255,59,48,.12)' : 'rgba(255,149,0,.12)', color: tone }}>
                        {d.tag}{imgList.length > 1 ? ` · ${imgList.length} 张` : ''}
                      </span>
                    </div>
                    <div style={{ marginTop: 3 }}>
                      <OriginLine origin={origin} audience="user" onOpenImage={(im) => setLightboxUrl(im.url)} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── 待处理：无效（2张配对但不在库/过期） ───────────────────────────── */}
      {invalids.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '10px 0 7px' }}>无效（号码不在有效派单库）</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {invalids.map(img => (
              <div key={img.id} style={{
                border: '1px solid var(--c-border)', borderRadius: 10,
                padding: '10px 14px', background: 'var(--c-surface)',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {/* 图片未删：显示缩略图；已删：显示占位图标 */}
                {!imagesDeleted ? (
                  <img src={img.thumbUrl || img.url} alt=""
                    style={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
                    onClick={() => setLightboxUrl(img.url)}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'rgba(255,59,48,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--c-danger)" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-danger)' }}>无法识别号码</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>图片内容未检测到手机号</div>
                </div>
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '4px 12px', flexShrink: 0 }}
                  onClick={() => onManualFill && onManualFill(img.id)}>
                  手动填写
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── 失败：识别不出 + OCR崩溃 + 单张缺配对 ─────────────────────────── */}

      {(faileds.length > 0 || notexts.length > 0 || singles.length > 0) && (
        <>
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', fontWeight: 600, margin: '10px 0 7px' }}>识别失败 / 无法配对</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

            {/* 单张缺配对 */}
            {singles.map(img => (
              <div key={img.id} style={{
                border: '1px solid var(--c-border)', borderRadius: 10,
                padding: '10px 14px', background: 'var(--c-surface)',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {!imagesDeleted ? (
                  <img src={img.thumbUrl || img.url} alt=""
                    style={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
                    onClick={() => setLightboxUrl(img.url)}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'rgba(255,149,0,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--c-accent)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-accent)' }}>
                    {img.ocrPhone || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>识别到号码但批次内仅1张，缺少配对图</div>
                </div>
                {/* 预填已识别号码，用户直接确认即可，无需重新输入 */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                    onClick={() => onManualFill && onManualFill(img.id, img.ocrPhone || '')}>
                    手动填写
                  </button>
                  {onReocr && (
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                      onClick={() => onReocr(img.id)}>
                      重新识别
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* 识别不出号码 */}
            {notexts.map(img => (
              <div key={img.id} style={{
                border: `1px solid ${img.userFillLocked ? 'var(--c-danger)' : 'var(--c-border)'}`,
                borderRadius: 10, padding: '10px 14px', background: 'var(--c-surface)',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {!imagesDeleted ? (
                  <img src={img.thumbUrl || img.url} alt=""
                    style={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
                    onClick={() => setLightboxUrl(img.url)}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'rgba(255,59,48,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--c-danger)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-danger)' }}>
                    {img.userFillLocked ? '填写验证失败（已锁定）' : '无法识别号码'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                    {img.userFillLocked
                      ? '填写的号码与图片内容不符，请联系管理员处理'
                      : (img.ocrFail ? `${img.ocrFail.label}。${img.ocrFail.hint}` : '图片内容未检测到手机号')}
                  </div>
                </div>
                {!img.userFillLocked && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                      onClick={() => onManualFill && onManualFill(img.id)}>
                      手动填写
                    </button>
                    {onReocr && (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                        onClick={() => onReocr(img.id)}>
                        重新识别
                      </button>
                    )}
                  </div>
                )}
                {img.userFillLocked && (
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--c-danger)' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    已锁定
                  </div>
                )}
              </div>
            ))}

            {/* OCR彻底失败 */}
            {faileds.map(img => (
              <div key={img.id} style={{
                border: '1px solid var(--c-border)', borderRadius: 10,
                padding: '10px 14px', background: 'var(--c-surface)',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                {!imagesDeleted ? (
                  <img src={img.thumbUrl || img.url} alt=""
                    style={{ width: 36, height: 64, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--c-border)', cursor: 'zoom-in' }}
                    onClick={() => setLightboxUrl(img.url)}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: 'var(--c-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--c-text-tertiary)" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)' }}>识别失败</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                    {img.ocrFail ? `${img.ocrFail.label}。${img.ocrFail.hint}` : '图片处理出错'}
                  </div>
                </div>
                {/* 超时/限流等瞬时故障会记为 failed（此前混在"识别不出号码"里，有手动填写/重新识别按钮），
                    这里必须保留同样的操作，否则用户无法自己重试。*/}
                {img.ocrFail && NO_USER_ACTION_KINDS.has(img.ocrFail.kind) ? (
                  <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--c-text-tertiary)' }}>请联系管理员</div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                      onClick={() => onManualFill && onManualFill(img.id)}>
                      手动填写
                    </button>
                    {onReocr && (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 12px' }}
                        onClick={() => onReocr(img.id)}>
                        重新识别
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

          </div>
        </>
      )}

      {pairMap.size === 0 && dupGroups.length === 0 && invalids.length === 0 && faileds.length === 0 && notexts.length === 0 && singles.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center', padding: '8px 0' }}>暂无识别结果</div>
      )}

      {/* ── 图片放大 Lightbox ──────────────────────────────────────── */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 999,
            background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxUrl} alt=""
            style={{
              maxWidth: '100%', maxHeight: '100%',
              borderRadius: 10, objectFit: 'contain',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
            width: 36, height: 36, borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 20, color: '#fff', lineHeight: 1,
          }}>✕</div>
        </div>
      )}
    </div>
  );
}
